import React, { useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../api/axiosClient";
import "../../styles/scoretable.css";

// const API_BASE = "http://localhost:8000/api";
const ENDPOINT = "/vw-cbg-scores/";

const CBGscoretable = () => {
  const tableRef = useRef();
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 const empty = [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        //  use apiClient instead of fetch
        const response = await apiClient.get(ENDPOINT);

        // axios automatically parses JSON
        setApiData(response.data.results || []); 
        setLoading(false);
      } catch (err) {
        console.error("API error:", err);
        setError("Failed to fetch data from API");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;


  const companies = apiData.map(item => item.organisation_name);
  const install_capacity_2024 = apiData.map(item => item.install_capacity_2024 || 0);
  const install_capacity_2023 = apiData.map(item => item.install_capacity_2023 || 0);
  const actual_prod_2024 = apiData.map(item => item.actual_prod_2024 || 0);
  const actual_prod_2023 = apiData.map(item => item.actual_prod_2023 || 0);
  const absolute_capex_percentage_score = apiData.map(item => item.absolute_capex_percentage_score || 0);


  const lost_injury_rate_2024 = apiData.map(item => item.lost_injury_rate_2024 || 0); // Assuming for year-on-year capacity
  const incident_rate_2024 = apiData.map(item => item.incident_rate_2024 || 0); // Assuming for capacity utilization
  const retail_outlets_2024 = apiData.map(item => item.retail_outlets_2024 || 0);
  const retail_outlets_2023 = apiData.map(item => item.retail_outlets_2023 || 0);

  const patents_filed_2024 = apiData.map(item => item.patents_filed_2024 || 0);
  const patents_national_2024 = apiData.map(item => item.patents_national_2024 || 0);
  const patents_international_2024 = apiData.map(item => item.patents_international_2024 || 0);
  const patents_commercial_2024 = apiData.map(item => item.patents_commercial_2024 || 0);

  // Scores from API
  const install_capacity_score = apiData.map(item => item.install_capacity_score || 0);
  const actual_production_score = apiData.map(item => item.actual_production_score || 0);
  const fetal_accident_rate_score = apiData.map(item => item.fetal_accident_rate_score || 0); // Assuming for safety index
  const lost_time_injuries_score = apiData.map(item => item.lost_time_injuries_score || 0);
  const total_record_incident_score = apiData.map(item => item.total_record_incident_score || 0);
  const patents_filed_score = apiData.map(item => item.patents_filed_score || 0);
  const national_score = apiData.map(item => item.national_score || 0);
  const international_score = apiData.map(item => item.international_score || 0);
  const patents_commercialized_score = apiData.map(item => item.patents_commercialized_score || 0);
  const absolute_capex_score=apiData.map(item => item.absolute_capex_score || 0)

  // Section totals
  const capex_investment_growth = apiData.map(item => item.capex_investment_growth);
  const install_investment_growth = apiData.map(item => item.install_investment_growth);
  const capacity_utilisation_growth= apiData.map(item => item.capacity_utilisation_growth);
  //const install_capacity_percentage_score= apiData.map(item => item.install_capacity_percentage_score);
  const total_record_incident_performance =apiData.map(item => item.total_record_incident_performance);
  const fetal_accident_rate_performance =apiData.map(item => item.fetal_accident_rate_performance);
  const lost_time_injuries_performance =apiData.map(item => item.lost_time_injuries_performance);
  // const yoy_improvement_score =apiData.map(item => item.yoy_improvement_score);
  const accident_rate_2024 =apiData.map(item => item.accident_rate_2024);
  const yoy_improvement_capacity_util_score =apiData.map(item => item.yoy_improvement_capacity_util_score);
  const yoy_improvement_capacity_util_2023_24 =apiData.map(item => item.yoy_improvement_capacity_util_2023_24);
  const yoy_improvement_capacity_util_2024_25 =apiData.map(item => item.yoy_improvement_capacity_util_2024_25);
  const total_score_per_company =apiData.map(item => item.total_score_per_company);
  const totalhourworked2024 =apiData.map(item => item.totalhourworked2024);



  const safety_total_score = apiData.map(item => item.safety_total_score || 0);
  const install_capacity_percentage_score = apiData.map(item => item.install_capacity_percentage_score || 0);

  const r_n_d_in_CBG_areas_score = apiData.map(item => item.r_n_d_in_CBG_areas_score || 0);
  const handleExport = () => {
    const table = tableRef.current;
    const wb = XLSX.utils.table_to_book(table, { sheet: "CBG Scores" });
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "CBG_Scores.xlsx");
  };

  return (
    <div className="table-box">
      <button onClick={handleExport} className="export-btn">
        Export to Excel
      </button>
      <table ref={tableRef} className="min-w-full border border-gray-300 shadow-md rounded-lg">
        <thead className="bg-blue-600 text-white">
          <tr>
            <th className="border px-3 py-2 cursor-pointer">S.No.</th>
            <th className="border px-3 py-2 cursor-pointer">Criteria</th>
            <th className="border px-3 py-2 cursor-pointer">Unit</th>
            <th className="border px-3 py-2 cursor-pointer">Marks</th>
            <th className="border px-3 py-2 cursor-pointer"></th>
            {companies.map((company) => (
              <th key={company} className="border px-3 py-2 cursor-pointer">
                {company}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {/* Section 1: CapEx Investment & Growth */}
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">1</td>
            <td className="border px-3 py-2"><strong>CapEx Investment & Growth</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">30</td>
            {capex_investment_growth.map((s, i) => (
              <td key={i} className="border px-3 py-2"><strong>{s.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">1.1</td>
            <td className="border px-3 py-2">Absolute CapEx</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">15</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">Rs/crore</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {retail_outlets_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2023-24</td>
            <td className="border px-3 py-2">Rs/crore</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {retail_outlets_2023.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Absolute CapEx Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {absolute_capex_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>

          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">1.2</td>
            <td className="border px-3 py-2">Year-on-Year increase in CapEx investment</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">15</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Year-on-Year increase in CapEx investment Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {absolute_capex_percentage_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>

          {/* Section 2: Installed Capacity & Growth */}
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">2</td>
            <td className="border px-3 py-2"><strong>Installed Capacity & Growth</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">30</td>
            {install_investment_growth.map((s, i) => (
              <td key={i} className="border px-3 py-2"><strong>{s.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">2.1</td>
            <td className="border px-3 py-2">Installed capacity</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">15</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">MTPA</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {install_capacity_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2023-24</td>
            <td className="border px-3 py-2">MTPA</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {install_capacity_2023.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Installed capacity score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {install_capacity_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">2.2</td>
            <td className="border px-3 py-2">Year-on-Year increase in Installed Capacity</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">15</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Year-on-Year increase in Installed Capacity Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {install_capacity_percentage_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>

          {/* Section 3: Capacity Utilisation & Growth */}
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">3</td>
            <td className="border px-3 py-2"><strong>Capacity Utilisation & Growth</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">20</td>
            {capacity_utilisation_growth.map((s, i) => (
              <td key={i} className="border px-3 py-2"><strong>{s.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">3.1</td>
            <td className="border px-3 py-2">Capacity Utilisation</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">12</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Actual Production</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">MTPA</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {actual_prod_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2023-24</td>
            <td className="border px-3 py-2">MTPA</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {actual_prod_2023.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Actual Production Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {actual_production_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">3.2</td>
            <td className="border px-3 py-2">Year-on-Year Improvement in Capacity Utilisation</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">8</td>
            {empty.map((v, i) => (
              <td key={i} className="border px-3 py-2">{isFinite(v) ? v.toFixed(2) : "Inf"}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">%</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {yoy_improvement_capacity_util_2024_25.map((v, i) => (
              <td key={i} className="border px-3 py-2">{isFinite(v) ? v.toFixed(2) : "Inf"}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2023-24</td>
            <td className="border px-3 py-2">%</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {yoy_improvement_capacity_util_2023_24.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Year-on-Year Improvement in Capacity Utilisation Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {yoy_improvement_capacity_util_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>

          {/* Section 4: Safety */}
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">4</td>
            <td className="border px-3 py-2"><strong>Safety</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">10</td>
            {safety_total_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">4.1</td>
            <td className="border px-3 py-2">Fatal Accident Rate (No. of Fatalities x 10,00,00,000) / Total hour worked in reporting period (2024-25)</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">4</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Number of fatalities</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {accident_rate_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Total Hours worked</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {totalhourworked2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>

          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Fatal Accident Rate</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {fetal_accident_rate_performance.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Fatal Accident Rate Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {fetal_accident_rate_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">4.2</td>
            <td className="border px-3 py-2">Lost Time Injury frequency (No. lost time injuries in reporting period x 10,00,000) / Total hour worked in reporting period (2024-25)</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">3</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Lost Time Injuries</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {lost_injury_rate_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Lost Time Injuries Frequency</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {lost_time_injuries_performance.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Lost Time Injuries Frequency Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {lost_time_injuries_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">4.3</td>
            <td className="border px-3 py-2">Total Recordable Incident rate (No.of OSHA recordable incidents x 2,00,000) / Total hour worked in reporting period (2024-25)</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">3</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Recordable Incidents</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {incident_rate_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">Total Recordable Incident Rate</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {total_record_incident_performance.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Total Recordable Incident Rate Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {total_record_incident_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>

          {/* Section 5: R&D in CBG Areas */}
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5</td>
            <td className="border px-3 py-2"><strong>R&D in CBG areas</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">10</td>
            {r_n_d_in_CBG_areas_score.map((s, i) => (
              <td key={i} className="border px-3 py-2"><strong>{s.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5.1</td>
            <td className="border px-3 py-2">Patents filed</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-2025</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_filed_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Patents filed Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_filed_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5.2</td>
            <td className="border px-3 py-2">Patents Granted</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5.2.1</td>
            <td className="border px-3 py-2">National</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">3</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_national_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>National Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {national_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5.2.2</td>
            <td className="border px-3 py-2">International</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">3</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-25</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_international_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>International Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {international_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2">5.3</td>
            <td className="border px-3 py-2">Patents Commercialized</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2</td>
            {companies.map((v, i) => (
              <td key={i} className="border px-3 py-2"></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">2024-2025</td>
            <td className="border px-3 py-2">No.</td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_commercial_2024.map((v, i) => (
              <td key={i} className="border px-3 py-2">{v.toFixed(2)}</td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Patents commercialized Score</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            {patents_commercialized_score.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
          <tr className="hover:bg-gray-100">
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"><strong>Total</strong></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2"></td>
            <td className="border px-3 py-2">100</td>
            {total_score_per_company.map((v, i) => (
              <td key={i} className="border px-3 py-2"><strong>{v.toFixed(2)}</strong></td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CBGscoretable;
